import { z } from 'zod';

export const INVESTMENT_CASH_CATEGORIES = [
  'CONTRIBUTION',
  'WITHDRAWAL',
  'DIVIDEND',
  'INCOME_TRANSFER',
  'INTEREST',
  'FEE',
  'CORPORATE_ACTION',
  'OTHER_INCOME',
  'UNCLASSIFIED',
] as const;

export const INVESTMENT_RETURN_PERIODS = ['1M', '3M', 'YTD', '1Y', 'SINCE_TRACKING'] as const;

export const InvestmentCashCategorySchema = z.enum(INVESTMENT_CASH_CATEGORIES);
export const InvestmentReturnPeriodSchema = z.enum(INVESTMENT_RETURN_PERIODS);

const Money = z.string();
const NullableMoney = z.string().nullable();

export const InvestmentPeriodReturnSchema = z.object({
  period: InvestmentReturnPeriodSchema,
  returnPct: NullableMoney,
  gain: NullableMoney,
  netFlows: NullableMoney,
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  unavailableReason: z.string().nullable(),
});

/**
 * Time-weighted return for one calendar year. `partial` means tracking began after 1 January,
 * so the figure covers startDate → endDate only; `yearToDate` marks the current, unfinished year.
 */
export const CalendarYearReturnSchema = z.object({
  year: z.number().int(),
  returnPct: NullableMoney,
  gain: NullableMoney,
  netFlows: NullableMoney,
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  partial: z.boolean(),
  yearToDate: z.boolean(),
  unavailableReason: z.string().nullable(),
});

export const AccountAnnualReturnsResponseSchema = z.object({
  accountId: z.string(),
  currency: z.string(),
  years: z.array(CalendarYearReturnSchema),
});

export const InvestmentReturnsBreakdownSchema = z.object({
  contributions: Money,
  withdrawals: Money,
  openingCash: NullableMoney,
  dividends: Money,
  incomeTransfers: Money,
  interest: Money,
  fees: Money,
  corporateActions: Money,
  otherIncome: Money,
  unclassified: Money,
  closedPositionsRealised: Money,
  closedPositionsCount: z.number().int(),
  closedPositionsExcludedCount: z.number().int(),
});

export const InvestmentReturnsIssueSchema = z.object({
  kind: z.enum([
    'OPENING_CASH',
    'NEGATIVE_OPENING_CASH',
    'OPENING_POSITIONS',
    'UNCLASSIFIED_CASH',
    'NO_HISTORY',
    'VALUATION_UNAVAILABLE',
    'ACCOUNT_EXCLUDED',
  ]),
  message: z.string(),
  amount: NullableMoney,
  items: z.array(z.string()),
});

const SinceInceptionSchema = z.object({
  status: z.enum(['COMPLETE', 'PARTIAL', 'UNAVAILABLE']),
  historyStart: z.string().nullable(),
  valuationDate: z.string().nullable(),
  currentValue: NullableMoney,
  netContributions: NullableMoney,
  gain: NullableMoney,
  gainPct: NullableMoney,
  annualisedReturnPct: NullableMoney,
  breakdown: InvestmentReturnsBreakdownSchema,
  issues: z.array(InvestmentReturnsIssueSchema),
  periods: z.array(InvestmentPeriodReturnSchema),
  trackingSince: z.string().nullable(),
  snapshotCount: z.number().int(),
});

export const InvestmentAccountReturnsSchema = SinceInceptionSchema.extend({
  accountId: z.string(),
  accountName: z.string(),
  currency: z.string(),
  calendarYears: z.array(CalendarYearReturnSchema),
  categories: z.array(
    z.object({
      category: InvestmentCashCategorySchema,
      count: z.number().int(),
      total: Money,
      examples: z.array(z.string()),
    }),
  ),
});

export const InvestmentCurrencyReturnsSchema = SinceInceptionSchema.extend({
  currency: z.string(),
  accountIds: z.array(z.string()),
});

export const InvestmentHoldingReturnSchema = z.object({
  status: z.enum(['RECONCILED', 'HISTORY_INCOMPLETE']),
  invested: NullableMoney,
  income: Money,
  realisedPnl: NullableMoney,
  totalReturn: NullableMoney,
  totalReturnPct: NullableMoney,
  unexplainedUnits: NullableMoney,
});

export type InvestmentCashCategory = z.infer<typeof InvestmentCashCategorySchema>;
export type InvestmentReturnPeriod = z.infer<typeof InvestmentReturnPeriodSchema>;
export type InvestmentPeriodReturn = z.infer<typeof InvestmentPeriodReturnSchema>;
export type CalendarYearReturn = z.infer<typeof CalendarYearReturnSchema>;
export type AccountAnnualReturnsResponse = z.infer<typeof AccountAnnualReturnsResponseSchema>;
export type InvestmentReturnsBreakdown = z.infer<typeof InvestmentReturnsBreakdownSchema>;
export type InvestmentReturnsIssue = z.infer<typeof InvestmentReturnsIssueSchema>;
export type InvestmentAccountReturns = z.infer<typeof InvestmentAccountReturnsSchema>;
export type InvestmentCurrencyReturns = z.infer<typeof InvestmentCurrencyReturnsSchema>;
export type InvestmentHoldingReturn = z.infer<typeof InvestmentHoldingReturnSchema>;
